/**
 * CabinetContext — состояние кабинета Рублокса (внутренней части после входа).
 *
 * Объединяет:
 *   - экономику (diamonds, rating) — аналог EconomyState.kt
 *   - выбранный скин (selectedSkin) — аналог AvatarState.kt; на вебе храним
 *     в localStorage (как Godot-десктоп — локальный выбор, бэк хранит только
 *     список купленных)
 *   - профиль (firstName, photo, level)
 *
 * Использование: const { diamonds, rating, refreshEconomy, ... } = useCabinet();
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import * as api from "../api/cabinet";
import {
  DEFAULT_FREE_SKIN,
  defaultSkinForGender,
  isValidSkin,
} from "../data/skinsCatalog";
import { CAB_THEMES, getStoredTheme, storeTheme } from "../theme/cabThemes";

const CabinetContext = createContext(null);
const SKIN_KEY = "rublox_selected_skin";

function readSelectedSkin() {
  try {
    const stored = localStorage.getItem(SKIN_KEY);
    // 2026-06-13: legacy-скины (skin_bacon-hair, skin_sigma-labubu и др.)
    // больше не существуют. Если в localStorage сохранён мусор — сбрасываем
    // на дефолтный Y-Bot (пол подставится позже из profile через effect).
    if (stored && isValidSkin(stored)) return stored;
    return DEFAULT_FREE_SKIN;
  } catch {
    return DEFAULT_FREE_SKIN;
  }
}
function writeSelectedSkin(folder) {
  try {
    localStorage.setItem(SKIN_KEY, folder);
  } catch {
    /* приватный режим */
  }
}

export function CabinetProvider({ children }) {
  const { isAuthenticated, user } = useAuth();

  const [diamonds, setDiamonds] = useState(null);
  const [stars, setStars] = useState(null); // донатная валюта (звёзды)
  const [rating, setRating] = useState(null);
  const [profile, setProfile] = useState(null);
  const [selectedSkin, setSelectedSkinState] = useState(readSelectedSkin);
  // Тема кабинета: мгновенно из localStorage, потом синхронизируется с
  // profile.app_theme (бэк — источник правды между устройствами).
  const [appTheme, setAppThemeState] = useState(getStoredTheme);
  const navigate = useNavigate();

  const econAbort = useRef(null);

  /* ── экономика: алмазы + звёзды + рейтинг ──────────────────────── */
  const refreshEconomy = useCallback(async () => {
    if (!isAuthenticated) return;
    if (econAbort.current) econAbort.current.abort();
    const ctrl = new AbortController();
    econAbort.current = ctrl;
    try {
      const [d, s, r] = await Promise.all([
        api.getMyDiamonds(ctrl.signal).catch(() => null),
        api.getMyStars(ctrl.signal).catch(() => null),
        api.getMyRating(ctrl.signal).catch(() => null),
      ]);
      if (d != null) setDiamonds(d);
      if (s != null) setStars(s);
      if (r != null) setRating(r);
    } catch {
      /* игнор */
    }
  }, [isAuthenticated]);

  /* ── профиль ───────────────────────────────────────────────────── */
  const refreshProfile = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const p = await api.getMyProfile();
      setProfile(p);
    } catch {
      /* игнор */
    }
  }, [isAuthenticated]);

  /* при входе — подтягиваем всё; при выходе — сбрасываем */
  useEffect(() => {
    if (isAuthenticated) {
      refreshEconomy();
      refreshProfile();
    } else {
      setDiamonds(null);
      setStars(null);
      setRating(null);
      setProfile(null);
    }
  }, [isAuthenticated, refreshEconomy, refreshProfile]);

  /* тема кабинета: бэк — источник правды (синхронизация между устройствами) */
  useEffect(() => {
    const serverTheme = profile?.app_theme;
    if (serverTheme && CAB_THEMES[serverTheme] && serverTheme !== appTheme) {
      setAppThemeState(serverTheme);
      storeTheme(serverTheme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  /* Примерка темы из магазина.
     ОТДЕЛЬНО от setAppTheme, потому что тот пишет в localStorage: примеренная
     тема «прилипла» бы после ухода со страницы, и человек получил бы
     оформление, которое не покупал. Здесь тема живёт только в памяти —
     F5 или уход из магазина её сбрасывают.
     Раньше кнопка «Примерить» вообще ничего не красила: она лишь показывала
     плашку-подсказку, то есть примерки не существовало.
     Объявлено ДО setAppTheme: тот сбрасывает примерку, а const не поднимается
     (получили бы обращение к переменной в мёртвой зоне). */
  const [previewTheme, setPreviewTheme] = useState(null);
  const tryOnTheme = useCallback((key) => {
    setPreviewTheme(key && CAB_THEMES[key] ? key : null);
  }, []);

  /* сменить тему локально (активацию на бэке делает ShopScreen) */
  const setAppTheme = useCallback((key) => {
    const k = CAB_THEMES[key] ? key : "classic";
    setAppThemeState(k);
    storeTheme(k);
    setPreviewTheme(null);   // выбор сделан — примерка больше не нужна
  }, []);

  /* при появлении профиля — если в localStorage НЕТ валидного выбора
   * (пусто или legacy-скин типа skin_bacon-hair), выставляем дефолт
   * по полу пользователя:
   *  - male → skin_x-bot (Икс-Бот)
   *  - female / null / undefined → skin_y-bot (Игрек-Бот)
   * Если юзер уже выбрал валидный новый скин — не трогаем. */
  useEffect(() => {
    if (!profile) return;
    let cancelled = false;
    (async () => {
      try {
        const stored = localStorage.getItem(SKIN_KEY);
        if (stored && isValidSkin(stored)) return; // валидный локальный выбор — не трогаем
        // localStorage пуст/невалиден → подтягиваем НАДЕТЫЙ скин из БД
        // (синхронизация между устройствами: телефон/сайт показывают одно).
        const userId = profile?.id || user?.id;
        if (userId) {
          const dbSkin = await api.getEquippedSkin(userId);
          if (cancelled) return;
          if (dbSkin && isValidSkin(dbSkin)) {
            setSelectedSkinState(dbSkin);
            writeSelectedSkin(dbSkin);
            return;
          }
        }
        // В БД тоже невалидный (legacy bacon) или пусто → дефолт по полу.
        const gender = profile.gender || user?.gender || null;
        const defaultId = defaultSkinForGender(gender);
        if (cancelled) return;
        setSelectedSkinState(defaultId);
        writeSelectedSkin(defaultId);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [profile, user]);

  /* ── выбранный скин ────────────────────────────────────────────── */
  /* Локально (state + localStorage) обновляем сразу — UI откликнется
   * мгновенно. В фоне шлём POST /equipped-skin чтобы синхронизировать
   * с rublox_equipped_skin в БД: тогда в плеере, в карточках "На сервере"
   * и в мультиплеере у всех будет правильный скин. Ошибки молча игнорим
   * (legacy юзеры могут не иметь скин в owned — 403 от бэка). */
  const setSelectedSkin = useCallback((folder) => {
    setSelectedSkinState(folder);
    writeSelectedSkin(folder);
    api.setEquippedSkin(folder).catch((e) => {
      // eslint-disable-next-line no-console
      console.warn("[CabinetContext] setEquippedSkin failed:", e?.message || e);
    });
  }, []);

  /* локальное изменение баланса (после покупки скина) без перезапроса */
  const setDiamondsLocal = useCallback((value) => {
    setDiamonds(value);
  }, []);
  const setStarsLocal = useCallback((value) => {
    setStars(value);
  }, []);

  // Кошелёк — отдельная страница /app/wallet (2026-07-09, раньше модалка).
  // openWallet оставлен в контексте: его зовут сайдбар и чипы шапки профиля.
  const openWallet = useCallback(() => navigate("/app/wallet"), [navigate]);

  const value = {
    // экономика
    diamonds,
    stars,
    rating,
    refreshEconomy,
    setDiamondsLocal,
    setStarsLocal,
    // кошелёк (страница /app/wallet)
    openWallet,
    // тема кабинета. effectiveTheme — то, что реально рисуется:
    // примерка временно побеждает выбранную тему.
    appTheme,
    setAppTheme,
    previewTheme,
    tryOnTheme,
    effectiveTheme: previewTheme || appTheme,
    // профиль
    profile,
    refreshProfile,
    // имя/аватар — из user (AuthContext) или profile
    displayName: profile?.firstName || user?.username || "Игрок",
    photo: api.profilePhotoSrc(profile?.photo || user?.photo || null),
    level: profile?.level || 0,
    userId: user?.userId || null,
    // скин
    selectedSkin,
    setSelectedSkin,
  };

  return (
    <CabinetContext.Provider value={value}>{children}</CabinetContext.Provider>
  );
}

export function useCabinet() {
  const ctx = useContext(CabinetContext);
  if (!ctx) {
    throw new Error("useCabinet должен использоваться внутри <CabinetProvider>");
  }
  return ctx;
}
